import dynamic from 'next/dynamic'
import { Switch } from 'react-router-dom'

import * as Page from '@/components/pages'
import { config } from '@/config'
import { User } from '@/core/auth'
import { handleChunkLoadError } from '@/lib/errorHandler'

import { PrivateRoute } from './PrivateRoute'
import { routes } from './routes'

type ConsoleRouterProps = {
  user: User | null
}

const ConsoleLiveSetting = dynamic<{}>(
  config.modules.liveStream
    ? import('@/components/pages/ConsoleLiveSetting')
        .then(m => m.ConsoleLiveSetting)
        .catch(async error => {
          await handleChunkLoadError(error)
          return () => null
        })
    : Promise.resolve(() => null),
  { ssr: false }
)
const ConsoleLiveDetail = dynamic<{}>(
  config.modules.liveStream
    ? import('@/components/pages/ConsoleLiveDetail')
        .then(m => m.ConsoleLiveDetail)
        .catch(async error => {
          await handleChunkLoadError(error)
          return () => null
        })
    : Promise.resolve(() => null),
  { ssr: false }
)
const ConsoleLiveFinish = dynamic<{}>(
  config.modules.liveStream
    ? import('@/components/pages/ConsoleLiveFinish')
        .then(m => m.ConsoleLiveFinish)
        .catch(async error => {
          await handleChunkLoadError(error)
          return () => null
        })
    : Promise.resolve(() => null),
  { ssr: false }
)
const ConsoleData = dynamic<{}>(
  import('@/components/pages/ConsoleData')
    .then(m => m.ConsoleData)
    .catch(async error => {
      await handleChunkLoadError(error)
      return () => null
    }),
  { ssr: false }
)

/**
 * This routing is for Console App.
 * To build artist management , we need to create its app router.
 */
export const ConsoleRouter = ({ user }: ConsoleRouterProps): JSX.Element => {
  return (
    <>
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleDashboard.path}
        element={<Page.ConsoleHome />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleUserDetail.path}
        element={<Page.ConsoleUserDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleUserOwnedItems.path}
        element={<Page.ConsoleUserOwnedItems />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleUserOwnedItemDetail.path}
        element={<Page.ConsoleUserOwnedItemDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleCampaigns.path}
        element={<Page.ConsoleCampaigns />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleCampaignDetail.path}
        element={<Page.ConsoleCampaignDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleMembership.path}
        element={<Page.ConsoleMembership />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleDigitalMembershipCard.path}
        element={<Page.ConsoleDigitalMembershipCard />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleDigitalMembershipDefaultCardDesignEdit.path}
        element={<Page.ConsoleDigitalMembershipDefaultCardDesignEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleDigitalMembershipCardDesignEdit.path}
        element={<Page.ConsoleDigitalMembershipCardDesignEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleFavoriteMemberSettings.path}
        element={<Page.ConsoleFavoriteMemberSettings />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleFavoriteMemberCreate.path}
        element={<Page.ConsoleFavoriteMemberCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleFavoriteMemberEdit.path}
        element={<Page.ConsoleFavoriteMemberEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleFans.path}
        element={<Page.ConsoleFans />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleMembershipDetail.path}
        element={<Page.ConsoleMembershipDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleOfficialInformations.path}
        element={<Page.ConsoleOfficialInformations />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleOfficialInformationCreate.path}
        element={<Page.ConsoleOfficialInformationCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleOfficialInformationEdit.path}
        element={<Page.ConsoleOfficialInformationEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsolePost.path}
        element={<Page.ConsolePosts />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleUnpublishedPosts.path}
        element={<Page.ConsoleUnpublishedPosts />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsolePostDetail.path}
        element={<Page.ConsolePostDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsolePostCreate.path}
        element={<Page.ConsolePostCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsolePostEdit.path}
        element={<Page.ConsolePostEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsolePostCategories.path}
        element={<Page.ConsolePostCategories />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleAccountSetting.path}
        element={<Page.ConsoleAccountSetting />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleMyProfileEdit.path}
        element={<Page.ConsoleMyProfileEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleRevenue.path}
        element={<Page.ConsoleRevenue />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleBankEdit.path}
        element={<Page.ConsoleBankEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleItems.path}
        element={<Page.ConsoleItems />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleItemCreate.path}
        element={<Page.ConsoleItemCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleItemEdit.path}
        element={<Page.ConsoleItemEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleItemOrders.path}
        element={<Page.ConsoleItemOrders />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleItemDetail.path}
        element={<Page.ConsoleItemDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleItemInformation.path}
        element={<Page.ConsoleItemInformation />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleItemInformationLimitUser.path}
        element={<Page.ConsoleItemInformationLimitUser />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleBusinessEdit.path}
        element={<Page.ConsoleBusinessEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleCollections.path}
        element={<Page.ConsoleCollections />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleCollectionCreate.path}
        element={<Page.ConsoleCollectionCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleCollectionEdit.path}
        element={<Page.ConsoleCollectionEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleCollectionDetail.path}
        element={<Page.ConsoleCollectionDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleCryptocurrencyWallet.path}
        element={<Page.ConsoleCryptocurrencyWallet />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleMissions.path}
        element={<Page.ConsoleMissions />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleMissionCreate.path}
        element={<Page.ConsoleMissionCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleMissionEdit.path}
        element={<Page.ConsoleMissionEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleMembershipExplain.path}
        element={<Page.ConsoleMembershipExplain />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleMembershipCreate.path}
        element={<Page.ConsoleMembershipCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleMembershipEdit.path}
        element={<Page.ConsoleMembershipEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleDmRooms.path}
        element={<Page.ConsoleDmRooms />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleDmRoomCreate.path}
        element={<Page.ConsoleDmRoomCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleDmRoomDetail.path}
        element={<Page.ConsoleDmRoomDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleShokufesEvents.path}
        element={<Page.ConsoleEvents />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleShokufesEventCreate.path}
        element={<Page.ConsoleEventCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleShokufesEventEdit.path}
        element={<Page.ConsoleEventEdit />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleShokufesEventDetail.path}
        element={<Page.ConsoleEventDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleQuantityManagements.path}
        element={<Page.ConsoleQuantityManagements />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleTicketReceives.path}
        element={<Page.ConsoleTicketReceives />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleTicketReceiveDetail.path}
        element={<Page.ConsoleTicketReceiveDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleOrders.path}
        element={<Page.ConsoleOrders />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleUsers.path}
        element={<Page.ConsoleUsers />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleUserOrders.path}
        element={<Page.ConsoleUserOrders />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleOrderDetail.path}
        element={<Page.ConsoleOrderDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleUsers.path}
        element={<Page.ConsoleUsers />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleData.path}
        element={<ConsoleData />}
      />
      <PrivateRoute
        exact
        isLogin={!!user?.isSigned}
        path={routes.ConsoleCampaignBannerSetting.path}
        element={<Page.ConsoleCampaignBannerSetting />}
      />
      <PrivateRoute
        exact
        isLogin={!!user}
        path={routes.ConsoleDeliveryManagement.path}
        element={<Page.ConsoleDeliveryManagement />}
      />
      <PrivateRoute
        exact
        isLogin={!!user}
        path={routes.ConsolePreSaleCreate.path}
        element={<Page.ConsolePreSaleCreate />}
      />
      <PrivateRoute
        exact
        isLogin={!!user}
        path={routes.ConsolePreSaleDetail.path}
        element={<Page.ConsolePreSaleDetail />}
      />
      <PrivateRoute
        exact
        isLogin={!!user}
        element={<Page.ConsolePreSaleEdit />}
        path={routes.ConsolePreSaleEdit.path}
      />
      <PrivateRoute
        exact
        isLogin={!!user}
        element={<Page.ConsoleLotteryManagementDetail />}
        path={routes.ConsoleLotteryManagementDetail.path}
      />
      <PrivateRoute
        exact
        isLogin={!!user}
        element={<Page.ConsoleLotteryCsvUpload />}
        path={routes.ConsoleLotteryCsvUpload.path}
      />
      <PrivateRoute
        exact
        isLogin={!!user}
        element={<Page.ConsoleLotteryManagements />}
        path={routes.ConsoleLotteryManagements.path}
      />
      {config.modules.liveStream && (
        <Switch>
          <PrivateRoute
            exact
            isLogin={!!user?.isSigned}
            path={routes.ConsoleLiveSetting.path}
            element={<ConsoleLiveSetting />}
          />
          <PrivateRoute
            exact
            isLogin={!!user?.isSigned}
            path={routes.ConsoleLiveDetail.path}
            element={<ConsoleLiveDetail />}
          />
          <PrivateRoute
            exact
            isLogin={!!user?.isSigned}
            path={routes.ConsoleLiveFinish.path}
            element={<ConsoleLiveFinish />}
          />
        </Switch>
      )}
    </>
  )
}
